import { maskOverlay, paintMaskStroke, placeRegionMask, type InkMaskResult } from './inkMask.ts';
import { viewportRectToSourceRect, type Rect } from './regionCrop.ts';
import { luminanceFromImageData } from './regionPropose.ts';

type Point = {x: number; y: number};
type Tool = 'select' | 'keep' | 'remove';

/** Local, disposable mask review. The source photo and conversion input stay intact.
 * Runs native-resolution analysis off the UI thread; cancels on source changes.
 */
export class InkMaskReview {
  private worker: Worker | null = null;
  private result: InkMaskResult | null = null;
  private initial: {mask: Uint8Array; confidence: Uint8Array} | null = null;
  private history: {mask: Uint8Array; confidence: Uint8Array}[] = [];
  private visible = false;
  private regionKey = '';
  private tool: Tool = 'select';
  private pointer: {id: number; last: Point} | null = null;
  private readonly tools = document.getElementById('inkMaskTools') as HTMLDivElement;
  private readonly toggle = document.getElementById('inkPreviewBtn') as HTMLButtonElement;
  private readonly mode = document.getElementById('inkMaskTool') as HTMLSelectElement;
  private readonly radius = document.getElementById('inkMaskBrush') as HTMLInputElement;
  private readonly undo = document.getElementById('inkMaskUndo') as HTMLButtonElement;

  constructor(private source: HTMLCanvasElement, private overlay: HTMLCanvasElement, private notify: (text: string) => void, private getRegion: () => Rect | null = () => null) {
    this.mode.addEventListener('change', () => this.setTool(this.mode.value as Tool));
    this.undo.addEventListener('click', () => {
      const previous = this.history.pop();
      if (!previous || !this.result) return;
      this.result.mask = previous.mask; this.result.confidence = previous.confidence;
      this.draw(); this.updateControls();
    });
    document.getElementById('inkMaskReset')?.addEventListener('click', () => {
      if (!this.result || !this.initial) return;
      this.remember();
      this.result.mask = this.initial.mask.slice(); this.result.confidence = this.initial.confidence.slice();
      this.draw(); this.updateControls();
    });
    document.getElementById('inkMaskDownload')?.addEventListener('click', () => this.download());
    overlay.addEventListener('pointerdown', ev => {
      if (!this.result || this.tool === 'select' || this.pointer || ev.button !== 0) return;
      ev.preventDefault(); this.remember();
      const point = this.point(ev);
      this.pointer = {id: ev.pointerId, last: point};
      overlay.setPointerCapture(ev.pointerId);
      this.paint(point, point); this.updateControls();
    });
    overlay.addEventListener('pointermove', ev => {
      if (!this.pointer || this.pointer.id !== ev.pointerId) return;
      ev.preventDefault();
      const point = this.point(ev);
      this.paint(this.pointer.last, point); this.pointer.last = point;
    });
    const end = (ev: PointerEvent) => {
      if (this.pointer?.id !== ev.pointerId) return;
      if (ev.type === 'pointerup') this.paint(this.pointer.last, this.point(ev));
      this.pointer = null;
      if (overlay.hasPointerCapture(ev.pointerId)) overlay.releasePointerCapture(ev.pointerId);
    };
    overlay.addEventListener('pointerup', end);
    overlay.addEventListener('pointercancel', end);
    overlay.addEventListener('lostpointercapture', () => { this.pointer = null; });
    new ResizeObserver(() => this.align()).observe(source);
  }

  toggleVisible(): void {
    if (this.visible) { this.hide(); return; }
    if (this.source.width <= 0 || this.source.height <= 0) return;
    const key = JSON.stringify(this.getRegion());
    if (key !== this.regionKey) { this.result = null; this.initial = null; this.history = []; this.regionKey = key; }
    this.visible = true;
    this.toggle.textContent = 'İşaretlemeyi Gizle';
    this.toggle.setAttribute('aria-pressed', 'true');
    if (this.result) { this.show(); return; }
    this.notify('Yazı adayları hesaplanıyor…');
    this.toggle.setAttribute('aria-busy', 'true');
    try {
      const ctx = this.source.getContext('2d');
      if (!ctx) throw new Error('Görsel okunamadı.');
      const {width, height} = this.source;
      // Bound allocation before readback, not after a large ImageData allocation.
      if (width * height > 12_000_000) throw new Error('Bu görsel çok büyük. İşaretleme için en fazla 12 megapiksellik bir görsel kullanın.');
      const selected = this.getRegion();
      const region = selected ? viewportRectToSourceRect({
        offsetX: 0, offsetY: 0, displayWidth: width, displayHeight: height,
        sourceWidth: width, sourceHeight: height,
      }, selected, 1) : {x: 0, y: 0, width, height};
      if (!region) throw new Error('Seçilen bölge görselin dışında; yeni bir bölge seçin.');
      const frame = ctx.getImageData(region.x, region.y, region.width, region.height);
      const luminance = luminanceFromImageData(frame.data, region.width, region.height);
      const worker = new Worker(new URL('./inkMask.worker.js', import.meta.url), {type: 'module'});
      this.worker = worker;
      worker.onmessage = ({data}: MessageEvent<{result?: InkMaskResult; error?: string}>) => {
        if (this.worker !== worker || !this.visible) return;
        worker.terminate(); this.worker = null;
        this.toggle.removeAttribute('aria-busy');
        if (data.error || !data.result) { this.fail(data.error || 'Maske oluşturulamadı.'); return; }
        this.result = placeRegionMask(data.result, width, height, region.x, region.y);
        this.initial = {mask: this.result.mask.slice(), confidence: this.result.confidence.slice()};
        this.draw(); this.show();
        this.notify(`Yazı adayları sarıyla işaretlendi (~%${(data.result.inkRatio * 100).toFixed(1)}). Seçime ekleyebilir veya seçimden çıkarabilirsiniz. Fotoğraf korunur; bu henüz vektör değildir. Masa ve spirali dışlamak için önce bölge seçebilirsiniz.`);
      };
      worker.onerror = () => { if (this.worker === worker) this.fail('İşaretleme başlatılamadı; tekrar deneyin.'); };
      worker.postMessage({luminance, width: region.width, height: region.height}, [luminance.buffer]);
    } catch (error) { this.fail(error instanceof Error ? error.message : 'Maske oluşturulamadı.'); }
  }

  /** Invalidate before loading another image; an old worker cannot repaint it. */
  resetSource(): void {
    this.hide(); this.result = null; this.initial = null; this.history = [];
    this.overlay.width = 0; this.overlay.height = 0;
  }

  hide(): void {
    this.worker?.terminate(); this.worker = null;
    this.visible = false; this.pointer = null;
    this.overlay.style.display = 'none'; this.tools.hidden = true;
    this.source.closest('#sourceCard')?.classList.remove('ink-mask-reviewing');
    this.toggle.textContent = 'Yazıları İşaretle';
    this.toggle.setAttribute('aria-pressed', 'false');
    this.toggle.removeAttribute('aria-busy');
    this.setTool('select');
  }

  align(): void {
    if (!this.visible || !this.result) return;
    this.overlay.style.left = `${this.source.offsetLeft}px`;
    this.overlay.style.top = `${this.source.offsetTop}px`;
    this.overlay.style.width = `${this.source.clientWidth}px`;
    this.overlay.style.height = `${this.source.clientHeight}px`;
  }

  private fail(message: string): void { this.hide(); this.notify(message); }
  private show(): void {
    this.overlay.style.display = 'block'; this.tools.hidden = false;
    this.source.closest('#sourceCard')?.classList.add('ink-mask-reviewing');
    this.align(); this.updateControls();
  }
  private setTool(tool: Tool): void {
    this.pointer = null; this.tool = tool; this.mode.value = tool;
    this.overlay.style.pointerEvents = tool === 'select' ? 'none' : 'auto';
    this.overlay.style.touchAction = tool === 'select' ? 'auto' : 'none';
    this.overlay.style.cursor = tool === 'select' ? '' : 'crosshair';
  }
  private point(ev: PointerEvent): Point {
    const rect = this.overlay.getBoundingClientRect();
    return {x: (ev.clientX - rect.left) * this.overlay.width / rect.width, y: (ev.clientY - rect.top) * this.overlay.height / rect.height};
  }
  private remember(): void {
    if (!this.result) return;
    this.history.push({mask: this.result.mask.slice(), confidence: this.result.confidence.slice()});
    if (this.history.length > 4) this.history.shift();
  }
  private updateControls(): void { this.undo.disabled = this.history.length === 0; }
  private draw(): void {
    if (!this.result) return;
    this.overlay.width = this.result.width; this.overlay.height = this.result.height;
    this.overlay.getContext('2d')?.putImageData(new ImageData(maskOverlay(this.result.mask, this.result.confidence), this.result.width, this.result.height), 0, 0);
  }
  private paint(from: Point, to: Point): void {
    if (!this.result) return;
    const {mask, confidence, width, height} = this.result;
    const radius = Math.max(1, Number(this.radius.value) || 6);
    paintMaskStroke(mask, width, height, from, to, radius, this.tool === 'keep');
    // Repaint only the changed source-pixel rectangle, never the entire photo.
    const x = Math.max(0, Math.floor(Math.min(from.x, to.x) - radius));
    const y = Math.max(0, Math.floor(Math.min(from.y, to.y) - radius));
    const right = Math.min(width, Math.ceil(Math.max(from.x, to.x) + radius) + 1);
    const bottom = Math.min(height, Math.ceil(Math.max(from.y, to.y) + radius) + 1);
    const cw = right - x, ch = bottom - y;
    if (cw <= 0 || ch <= 0) return;
    const patch = new Uint8Array(cw * ch);
    const patchConfidence = new Uint8Array(cw * ch);
    for (let py = 0; py < ch; py++) for (let px = 0; px < cw; px++) {
      const i = (py + y) * width + px + x, j = py * cw + px;
      if (mask[i] && !confidence[i]) confidence[i] = 255;
      patch[j] = mask[i]; patchConfidence[j] = confidence[i];
    }
    this.overlay.getContext('2d')?.putImageData(new ImageData(maskOverlay(patch, patchConfidence), cw, ch), x, y);
  }
  private download(): void {
    if (!this.result) return;
    const {width, height, mask} = this.result;
    const canvas = document.createElement('canvas'); canvas.width = width; canvas.height = height;
    const data = new Uint8ClampedArray(mask.length * 4);
    for (let i = 0; i < mask.length; i++) {
      const value = mask[i] ? 255 : 0;
      data[i*4] = value; data[i*4+1] = value; data[i*4+2] = value; data[i*4+3] = 255;
    }
    canvas.getContext('2d')?.putImageData(new ImageData(data, width, height), 0, 0);
    canvas.toBlob(blob => {
      if (!blob) return;
      const url = URL.createObjectURL(blob), link = document.createElement('a');
      link.href = url; link.download = 'yazi-maskesi.png'; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    }, 'image/png');
  }
}
